/**
 * The three legal documents the footer promises, and that reviewsjet.com
 * already ranks for.
 *
 * ===========================================================================
 * These are the live texts, not a rewrite
 * ===========================================================================
 * `/privacy-policy`, `/terms-of-service` and `/cookie-policy` are indexed URLs.
 * The copy here is the copy the outgoing Next site publishes — last updated
 * 26 November 2023 — so a visitor who bookmarked the live page, or a crawler
 * that already has it, finds the same agreement rather than a quieter paraphrase.
 *
 * Contact is `hello@reviewsjet.com`. The live site currently prints
 * `[email protected]` because Cloudflare's obfuscation failed.
 */

export type Inline =
  | string
  | { href: string; label: string; external?: boolean }
  | { bold: true; text: string };

export type ListItem =
  | string
  | Inline[]
  | { parts: Inline[]; children?: ListItem[] };

export type LegalBlock =
  | { type: 'p'; parts: Inline[] }
  | { type: 'list'; items: ListItem[] }
  | { type: 'h3'; text: string }
  | { type: 'address'; lines: Array<string | Inline[]> };

export type LegalSection = {
  id: string;
  title: string;
  blocks: LegalBlock[];
};

export type LegalDoc = {
  path: '/privacy-policy' | '/terms-of-service' | '/cookie-policy';
  title: string;
  description: string;
  updated: string;
  showToc: boolean;
  sections: LegalSection[];
};

export function para(...parts: Inline[]): LegalBlock {
  return { type: 'p', parts };
}

export function bullets(...items: ListItem[]): LegalBlock {
  return { type: 'list', items };
}

export function heading(text: string): LegalBlock {
  return { type: 'h3', text };
}

const CONTACT = {
  href: 'mailto:hello@reviewsjet.com',
  label: 'hello@reviewsjet.com',
  external: true,
} as const;

export const PRIVACY: LegalDoc = {
  path: '/privacy-policy',
  title: 'Privacy Policy',
  description:
    'Learn how ReviewsJet collects, uses, and protects your data. Our privacy policy outlines your rights and our commitment to safeguarding your information.',
  updated: 'November 26th, 2023',
  showToc: false,
  sections: [
    {
      id: 'introduction',
      title: 'Introduction',
      blocks: [
        para(
          'Welcome to ReviewsJet, a service provided by FlexByt, Inc. We value your privacy and are committed to protecting your personal data. This privacy policy explains how we collect, use, disclose, and safeguard your information when you use our services.'
        ),
      ],
    },
    {
      id: 'consent',
      title: 'Consent',
      blocks: [
        para(
          'By using our website, you hereby consent to our Privacy Policy and agree to its terms.'
        ),
      ],
    },
    {
      id: 'information-we-collect',
      title: 'Information we collect',
      blocks: [
        bullets(
          {
            parts: [
              { bold: true, text: "Clients' Information" },
              ': This refers to the data we collect from businesses that use our services. It includes:',
            ],
            children: [
              [
                { bold: true, text: 'Names' },
                ': The names of the business representatives who register or communicate with us.',
              ],
              [
                { bold: true, text: 'Email Addresses' },
                ': The email addresses provided by the business representatives for account setup, support, and communication purposes.',
              ],
            ],
          },
          {
            parts: [
              { bold: true, text: "Clients' Customers' Information" },
              ": This pertains to the data we collect from our clients' customers to facilitate the review process. It includes:",
            ],
            children: [
              [
                { bold: true, text: 'Names' },
                ': The names of the customers who have purchased products or services from our clients. This helps us personalize review requests and display verified reviews.',
              ],
              [
                { bold: true, text: 'Email Addresses' },
                ': The email addresses of the customers, which are used to send personalized review requests and follow-up communications.',
              ],
              [
                { bold: true, text: 'Order History' },
                ': Information about the products or services that customers have purchased from our clients. This data helps us tailor review requests to specific transactions, making the review process relevant and accurate.',
              ],
            ],
          }
        ),
      ],
    },
    {
      id: 'how-we-collect',
      title: 'Information we collect',
      blocks: [
        para('We collect information through:'),
        bullets(
          'Online forms on our website',
          'Cookies and similar tracking technologies',
          'Third-party integrations with e-commerce platforms'
        ),
      ],
    },
    {
      id: 'use',
      title: 'Use of Collected Information',
      blocks: [
        para('We use the information we collect in various ways, including to:'),
        bullets(
          'Sending personalized emails and review requests',
          "Displaying testimonials and reviews on clients' websites",
          'Communicate with you, either directly or through one of our partners, including for customer service, to provide you with updates and other information relating to the website, and for marketing and promotional purposes',
          'Send you emails'
        ),
      ],
    },
    {
      id: 'sharing',
      title: 'Data Sharing',
      blocks: [
        para(
          'We may share your information with legal authorities if required by law or in the event of a merger.'
        ),
      ],
    },
    {
      id: 'protection',
      title: 'Data Protection Measures',
      blocks: [
        para('We implement various security measures, including:'),
        bullets(
          'Encryption of sensitive data',
          'Access controls to limit access to personal data'
        ),
      ],
    },
    {
      id: 'breach',
      title: 'Data Breach Procedures',
      blocks: [
        para('In the event of a data breach, we will:'),
        bullets(
          'Notify affected individuals within 72 hours',
          'Take steps to mitigate the breach and prevent future occurrences'
        ),
      ],
    },
    {
      id: 'rights',
      title: 'User Rights',
      blocks: [
        para('Users have the right to:'),
        bullets(
          'Access their personal data',
          'Correct any inaccuracies',
          'Request deletion of their data'
        ),
        para('To exercise these rights, please contact us at ', CONTACT, '.'),
      ],
    },
    {
      id: 'cookies',
      title: 'Cookies and Tracking Technologies',
      blocks: [
        para(
          'We use cookies to enhance your experience. You can manage or opt out of cookies through your browser settings. For more information on cookies and how we use them, please refer to our ',
          { href: '/cookie-policy', label: 'Cookie Policy' },
          '.'
        ),
      ],
    },
    {
      id: 'retention',
      title: 'Data Retention',
      blocks: [
        para('We retain personal data for a period of 3 months.'),
      ],
    },
    {
      id: 'changes',
      title: 'Changes to This Privacy Policy',
      blocks: [
        para(
          'We may update this privacy policy from time to time. Any changes will be communicated via email and will be effective as of the date displayed on our web page.'
        ),
      ],
    },
    {
      id: 'contact',
      title: 'Contact Us',
      blocks: [
        para(
          'If you have any questions or concerns about this privacy policy, please contact us at ',
          CONTACT,
          '.'
        ),
      ],
    },
  ],
};

export const COOKIES: LegalDoc = {
  path: '/cookie-policy',
  title: 'Cookie Policy',
  description:
    'ReviewsJet uses cookies to improve your experience. Learn what we use and how to manage them in our cookie policy.',
  updated: 'November 26th, 2023',
  showToc: false,
  sections: [
    {
      id: 'introduction',
      title: 'Introduction',
      blocks: [
        para(
          'At ReviewsJet, a service provided by FlexByt, Inc., we use cookies and similar tracking technologies to enhance your experience on our website and improve our services. This Cookie Policy explains what cookies are, how we use them, and how you can manage your cookie preferences.'
        ),
      ],
    },
    {
      id: 'what-are-cookies',
      title: 'What Are Cookies?',
      blocks: [
        para(
          "Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a website. They help the website remember your actions and preferences over a period of time, so you don't have to re-enter them whenever you come back to the site or browse from one page to another."
        ),
      ],
    },
    {
      id: 'types',
      title: 'Types of Cookies We Use',
      blocks: [
        para('We use the following types of cookies on our website:'),
        bullets(
          [
            { bold: true, text: 'Essential Cookies' },
            ': These cookies are necessary for the website to function properly. They enable you to navigate the site and use its features, such as accessing secure areas.',
          ],
          [
            { bold: true, text: 'Performance Cookies' },
            ': These cookies collect information about how you use our website, such as which pages you visit most often. This data helps us improve the site’s performance and user experience.',
          ],
          [
            { bold: true, text: 'Functional Cookies' },
            ': These cookies allow the website to remember choices you make (such as your username, language, or the region you are in) and provide enhanced, more personal features. One first-party cookie (rj_attr, kept for 60 days) remembers which page, link or campaign first brought you to reviewsjet.com, so that if you later create an account we know how you found us. It contains no personal information.',
          ],
          [
            { bold: true, text: 'Targeting/Advertising Cookies' },
            ': These cookies are used to deliver advertisements more relevant to you and your interests. They are also used to limit the number of times you see an ad and help measure the effectiveness of advertising campaigns.',
          ],
          [
            { bold: true, text: 'Third-Party Cookies' },
            ': In addition to our own cookies, we may also use various third-party cookies to report usage statistics of the website and deliver advertisements.',
          ]
        ),
      ],
    },
    {
      id: 'how-we-use',
      title: 'How We Use Cookies',
      blocks: [
        para('We use cookies to:'),
        bullets(
          'Improve the functionality and performance of our website',
          'Provide a personalized user experience',
          'Analyze website traffic and usage patterns',
          'Deliver targeted advertisements',
          'Enhance security and prevent fraudulent activities'
        ),
      ],
    },
    {
      id: 'managing',
      title: 'Managing Your Cookie Preferences',
      blocks: [
        para(
          'You have the right to decide whether to accept or reject cookies. You can set your web browser to refuse cookies or to alert you when cookies are being sent. The following links provide information on how to manage cookies on popular browsers:'
        ),
        bullets(
          [
            {
              href: 'https://support.google.com/chrome/answer/95647?hl=en&co=GENIE.Platform%3DDesktop',
              label: 'Google Chrome',
              external: true,
            },
          ],
          [
            {
              href: 'https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop?redirectslug=enable-and-disable-cookies-website-preferences&redirectlocale=en-US',
              label: 'Mozilla Firefox',
              external: true,
            },
          ],
          [
            {
              href: 'https://support.microsoft.com/en-us/windows/manage-cookies-in-microsoft-edge-view-allow-block-delete-and-use-168dab11-0753-043d-7c16-ede5947fc64d',
              label: 'Microsoft Edge',
              external: true,
            },
          ],
          [
            {
              href: 'https://support.apple.com/en-us/105082',
              label: 'Safari',
              external: true,
            },
          ]
        ),
        para(
          'Please note that if you choose to disable cookies, some parts of our website may not function properly.'
        ),
      ],
    },
    {
      id: 'changes',
      title: 'Changes to This Cookie Policy',
      blocks: [
        para(
          'We may update this Cookie Policy from time to time. Any changes will be communicated via email and will be effective as of the date displayed on our web page.'
        ),
      ],
    },
    {
      id: 'contact',
      title: 'Contact Us',
      blocks: [
        para(
          'If you have any questions or concerns about our use of cookies, please contact us at ',
          CONTACT,
          '.'
        ),
      ],
    },
  ],
};

